type EasyFeatureManagerConfig = {};

export class EasyFeatureManager {
	private storage: EasyFeatureManagerStorage;

	constructor(featureFlags: EasyFeatureManagerConfig, cacheVersion?: string) {
		if (typeof cacheVersion !== "undefined") {
			this.resetStorage(cacheVersion);
		}

		this.storage = new EasyFeatureManagerStorage(featureFlags);
	}

	isEnabled(featureName: string): boolean {
		var isEnabled = this.storage.getItem(featureName);

		if (typeof isEnabled === "undefined") {
			return false;
		}

		return isEnabled;
	}

	enable(featureName: string): void {
		this.toggleFeature(featureName, true);
	}

	disable(featureName: string): void {
		this.toggleFeature(featureName, false);
	}

	private toggleFeature(featureName: string, isEnabled: boolean): void {
		var feature = this.storage.getItem(featureName);

		if (typeof feature === "undefined") {
			throw new Error(`Feature '${featureName}' is not defined.`);
		}

		this.storage.setItem(featureName, isEnabled);
	}

	/**
	 * Resets the storage at the specified date.
	 * Allows to reset users' localstorages to default values without asking them to do that manually.
	 * As a `cacheVersion` put cache version of feature flags.
	 * @param cacheVersion The version of cache. If cache version is different from the one stored in the local storage, the storage will be reset.
	 * @returns `true` if the storage was successfully reset, `false` otherwise.
	 */
	resetStorage(cacheVersion: string): boolean {
		const cacheVersionPrev = window.localStorage.getItem(
			"easyFeatureManager-version",
		);

		if (cacheVersionPrev === cacheVersion) {
			return false;
		}

		window.localStorage.removeItem("easyFeatureManager");
		window.localStorage.setItem("easyFeatureManager-version", cacheVersion);

		return true;
	}
}

class EasyFeatureManagerStorage {
	private map: Map<string, boolean>;

	constructor(featureFlags: EasyFeatureManagerConfig) {
		this.map = new Map();

		// merge provided settings with stored ones
		var storedSettings = this.restoreStorage();

		Object.entries(featureFlags).forEach(
			([configKey, configValue]: [string, any]) => {
				const storedValue = storedSettings.get(configKey);

				if (typeof storedValue === "undefined") {
					this.map.set(configKey, configValue);
				} else {
					this.map.set(configKey, storedValue);
				}
			},
		);

		this.saveStorage();
	}

	private restoreStorage(): Map<string, boolean> {
		const storedValue = window.localStorage.getItem("easyFeatureManager");

		return storedValue !== null
			? new Map(JSON.parse(storedValue))
			: new Map();
	}

	private saveStorage(): void {
		const storedValue = JSON.stringify(Array.from(this.map.entries()));
		window.localStorage.setItem("easyFeatureManager", storedValue);
	}

	getItem(name: string): boolean | undefined {
		return this.map.get(name);
	}

	setItem(name: string, value: boolean): void {
		this.map.set(name, value);

		this.saveStorage();
	}
}
